/**
 * Stylesheet for the screens that live *outside* a run — title and settings.
 *
 * Separate from `style.ts` for the same reason `runSetup.ts` keeps its own: the
 * in-run HUD must not carry a byte of CSS for menus it never shows. Every
 * colour comes from the `.ui-root` custom properties, which is what lets the
 * high-contrast setting reach these screens too without knowing they exist.
 */

const STYLE_ID = 'noitroidvania-shell-style';

const SHELL_CSS = /* css */ `
.sh-layer {
  position: absolute; inset: 0;
  display: grid; place-items: center;
  /* Above every run overlay. The event ticker (6), the altar panel (8) and the
     debug readout (50) set their own z-index inline in main.ts, so a settings
     screen at the default z-index had the ticker printing through it. */
  z-index: 60;
  background:
    radial-gradient(120% 80% at 50% 8%, #14161f 0%, var(--ui-bg) 62%, #040407 100%);
  overflow: auto;
}

.sh-box {
  width: min(760px, calc(100vw - 32px));
  padding: 40px 0 32px;
  display: flex; flex-direction: column; gap: 22px;
}

/* ---- title ---- */
.sh-brand { text-align: center; display: flex; flex-direction: column; gap: 10px; }
.sh-brand h1 {
  margin: 0;
  font-size: clamp(24px, 6.2vw, 46px);
  letter-spacing: .38em; text-indent: .38em;
  text-transform: uppercase; font-weight: normal;
  color: var(--ui-accent);
  text-shadow: 0 0 26px rgb(from var(--ui-accent) r g b / .22);
}
.sh-rule { height: 1px; background: linear-gradient(90deg, transparent, var(--ui-line-hot), transparent); }
.sh-tag {
  color: var(--ui-fg-dim); font-size: calc(var(--ui-fs) * .95);
  line-height: 1.65; max-width: 56ch; margin: 0 auto;
}
.sh-summary {
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .85);
  letter-spacing: .12em; text-transform: uppercase;
}

/* ---- menu ---- */
/* A grid of [label | note] columns, shared by every row via subgrid: the
   notes align to the widest label rather than to a guessed \`min-width\`,
   which "MOVEMENT LAB" plus .2em letter-spacing had already outgrown. */
.sh-menu { display: grid; grid-template-columns: max-content 1fr; gap: 4px 0; }
.sh-menu .sh-item { display: grid; grid-column: 1 / -1; grid-template-columns: subgrid;
  column-gap: 12px; }
.sh-item {
  display: flex; align-items: baseline; gap: 12px;
  padding: 9px 14px;
  border: 1px solid transparent;
  background: rgb(from var(--ui-panel) r g b / .5);
  cursor: pointer; text-align: left; width: 100%;
  font: inherit; color: var(--ui-fg-dim);
}
.sh-item b {
  font-weight: normal; letter-spacing: .2em; text-transform: uppercase;
  color: var(--ui-fg);
}
.sh-item i { font-style: normal; color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .88); }
.sh-item[data-on="1"] { border-color: var(--ui-accent); background: var(--ui-panel-2); }
.sh-item[data-on="1"] b { color: var(--ui-accent); }
.sh-item:hover { border-color: var(--ui-line-hot); }

/* ---- settings rows ---- */
.sh-head {
  display: flex; align-items: baseline; gap: 12px;
  border-bottom: 1px solid var(--ui-line); padding-bottom: 8px;
}
.sh-head h2 {
  margin: 0; font-size: var(--ui-fs); font-weight: normal;
  letter-spacing: .34em; text-transform: uppercase; color: var(--ui-accent);
}
.sh-rows { display: flex; flex-direction: column; gap: 3px; }
.sh-row {
  display: grid; grid-template-columns: 1fr auto;
  gap: 4px 14px; align-items: baseline;
  padding: 9px 14px;
  border: 1px solid transparent; background: rgb(from var(--ui-panel) r g b / .5);
  cursor: pointer; text-align: left; width: 100%;
  font: inherit; color: var(--ui-fg);
}
.sh-row[data-on="1"] { border-color: var(--ui-accent); background: var(--ui-panel-2); }
.sh-row:hover { border-color: var(--ui-line-hot); }
.sh-row__name { letter-spacing: .16em; text-transform: uppercase; }
.sh-row__val {
  color: var(--ui-accent); letter-spacing: .14em; text-transform: uppercase;
  justify-self: end; white-space: nowrap;
}
/* An action with no key left is not an error, it is a state the player can
   reach by giving its key to something else — so it is coloured as a warning
   and never silently as a key. */
.sh-row__val[data-unbound="1"] { color: var(--ui-warn); }
.sh-row__why {
  grid-column: 1 / -1;
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .86); line-height: 1.55;
}
.sh-note {
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .84); line-height: 1.7;
  border-left: 1px solid var(--ui-line); padding-left: 12px;
}
.sh-note b { color: var(--ui-fg-dim); font-weight: normal; }

/* ---- keys ---- */
.sh-keys {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2px 18px;
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .86);
}
.sh-keys span b { color: var(--ui-fg-dim); font-weight: normal; }
.sh-foot {
  display: flex; gap: 18px; justify-content: center;
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .82);
  letter-spacing: .12em; text-transform: uppercase;
}
`;

/** Idempotent, exactly like `injectStyle` — two screens must not duplicate it. */
export function injectShellStyle(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const s = doc.createElement('style');
  s.id = STYLE_ID;
  s.textContent = SHELL_CSS;
  doc.head.appendChild(s);
}
